import * as React from "react";
import { Upload, X, File } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/hooks/useI18n";

interface FileData {
  name: string;
  url: string;
}

interface UploadMultiFilesProps {
  files: FileData[];
  onFilesChange: (files: FileData[]) => void;
  itineraryId: string;
  activityId?: string; // Optional: if provided, saves to activities-data path
  label?: string;
  maxFiles?: number;
  accept?: string;
  className?: string;
  /** Sola lettura: nasconde il pulsante di upload e la rimozione file. */
  readOnly?: boolean;
}

const UploadMultiFiles = React.forwardRef<HTMLDivElement, UploadMultiFilesProps>(
  ({
    files,
    onFilesChange,
    itineraryId,
    activityId,
    label = "Upload Files",
    maxFiles,
    accept,
    className,
    readOnly = false,
    ...props
  }, ref) => {
    const { t } = useI18n();
    const [uploading, setUploading] = React.useState(false);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFiles = event.target.files;
      if (!uploadedFiles || uploadedFiles.length === 0) return;

      setUploading(true);
      const uploadedData: FileData[] = [];

      try {
        for (const file of Array.from(uploadedFiles)) {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
          // Se è presente activityId, salva in activities, altrimenti in docs
          const filePath = activityId
            ? `itineraries/${itineraryId}/activities/${activityId}/docs/${fileName}`
            : `itineraries/${itineraryId}/docs/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('p2b')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error('Upload error:', error);
            alert(`Failed to upload ${file.name}: ${error.message}`);
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('p2b')
            .getPublicUrl(filePath);

          uploadedData.push({
            name: file.name,
            url: publicUrl
          });
        }

        const newFiles = [...files, ...uploadedData];
        const finalFiles = maxFiles ? newFiles.slice(0, maxFiles) : newFiles;
        onFilesChange(finalFiles);
      } catch (error) {
        console.error('Error uploading files:', error);
        alert('An error occurred while uploading files');
      } finally {
        setUploading(false);
      }
    };

    const removeFile = async (index: number) => {
      const fileUrl = files[index].url;

      // Try to delete from storage if it's a Supabase URL
      if (fileUrl && fileUrl.includes('supabase')) {
        try {
          // Extract file path from URL
          const urlParts = fileUrl.split('/storage/v1/object/public/p2b/');
          if (urlParts.length > 1) {
            const filePath = urlParts[1];
            await supabase.storage
              .from('p2b')
              .remove([filePath]);
          }
        } catch (error) {
          console.error('Error deleting file from storage:', error);
        }
      }

      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
    };

    const isMaxReached = maxFiles ? files.length >= maxFiles : false;

    return (
      <div ref={ref} className={className} {...props}>
        {!readOnly && (
          <div>
            <Label htmlFor="file-upload" className="text-sm">{label}</Label>
            <div className="mt-2">
              <input
                id="file-upload"
                type="file"
                multiple
                accept={accept}
                onChange={handleFileUpload}
                className="hidden"
                disabled={isMaxReached}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => document.getElementById('file-upload')?.click()}
                disabled={isMaxReached || uploading}
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? "Uploading..." : isMaxReached ? `Max ${maxFiles} files` : "Choose Files"}
              </Button>
            </div>
          </div>
        )}

        {/* Files List */}
        {files && files.length > 0 ? (
          <div className="space-y-2 mt-4">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                <div className="flex items-center space-x-2">
                  <File className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{file.name}</span>
                </div>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          readOnly && <p className="text-sm text-gray-400 italic">{t('modals.noFiles')}</p>
        )}
      </div>
    );
  }
);

UploadMultiFiles.displayName = "UploadMultiFiles";

export { UploadMultiFiles };
export type { FileData };
